/**
 * NewProjectEnvironmentDialog - «Nuevo entorno del proyecto», with «Copiar desde…».
 *
 * Two ways to get an environment: from the project's base branch and its setup
 * hook, which is what `qa` was, or copied from one that already exists, which is
 * what `staging` usually wants to be. The copy is not "the same branch": what
 * makes an environment useful is the commit it sits on, the rows in its
 * database and the values in its `.env`, so the three are separate choices and
 * all three travel to the server, which is the only place that can honour them.
 *
 * In F1 this creates the environment directly. From F2 it will open a thread
 * with an agent and the `qa.clonePrompt` instead, so the copy leaves a record —
 * the same reason there is no HTTP route for syncing.
 */
import { useState } from "react";

import {
  DEFAULT_ENVIRONMENT_CLONE_CODE_MODE,
  DEFAULT_ENVIRONMENT_CLONE_DATA_MODE,
  type EnvironmentCloneCodeMode,
  type EnvironmentCloneDataMode,
  type EnvironmentId,
  type ProjectId,
} from "@taskin-code/contracts";

import { type AdminEnvironmentRow, EnvironmentsClient } from "../../auth/EnvironmentsClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { validateEnvironmentName } from "./projectEnvironments.logic";
import { Field, ModalShell } from "./settingsModal";

const CODE_LABELS: Record<EnvironmentCloneCodeMode, string> = {
  "same-sha": "El mismo commit que el origen",
  "branch-tip": "La punta de la rama que sigue el origen",
};

const DATA_LABELS: Record<EnvironmentCloneDataMode, string> = {
  "fresh-snapshot": "Copia fresca de sus datos de ahora",
  "latest-snapshot": "Su última copia guardada",
  baseline: "Su baseline",
  none: "Sin datos: sólo lo que deje el hook de preparación",
};

const labelOf = (row: AdminEnvironmentRow): string =>
  row.kind === "project" ? (row.name ?? row.branch) : `personal · ${row.branch}`;

export function NewProjectEnvironmentDialog(props: {
  readonly projectId: ProjectId;
  readonly projectName: string;
  readonly baseBranch: string | null;
  /** Live project environments, so a name already in use is refused before the request. */
  readonly existing: ReadonlyArray<AdminEnvironmentRow>;
  /** Environments this one could be copied from: same project, ready. */
  readonly sources: ReadonlyArray<AdminEnvironmentRow>;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [copyFrom, setCopyFrom] = useState(false);
  const [originId, setOriginId] = useState<string>(props.sources[0]?.id ?? "");
  const [code, setCode] = useState<EnvironmentCloneCodeMode>(DEFAULT_ENVIRONMENT_CLONE_CODE_MODE);
  const [data, setData] = useState<EnvironmentCloneDataMode>(DEFAULT_ENVIRONMENT_CLONE_DATA_MODE);
  const [config, setConfig] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taken = props.existing
    .map((row) => row.name)
    .filter((entry): entry is string => entry !== null);
  const origin = props.sources.find((row) => row.id === originId) ?? null;

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    const invalid = validateEnvironmentName(name, taken);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    if (copyFrom && origin === null) {
      setError("Elige de qué entorno copiar, o desactiva «Copiar desde».");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await EnvironmentsClient.provisionProject({
        projectId: props.projectId,
        name: name.trim(),
        ...(copyFrom && origin !== null
          ? {
              cloneFrom: {
                environmentId: origin.id as EnvironmentId,
                code,
                data,
                config,
              },
            }
          : {}),
      });
      props.onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo crear el entorno");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Nuevo entorno del proyecto ${props.projectName}`} onClose={props.onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label="Nombre">
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            placeholder="qa"
            autoComplete="off"
            spellCheck={false}
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Va a la rama del entorno y a su hostname de preview, así que sólo minúsculas, dígitos y
            guiones.
          </p>
        </Field>

        <label className="flex items-center justify-between gap-3 rounded-md border border-border/70 px-3 py-2 text-sm">
          <span className="min-w-0">
            <span className="block truncate">Copiar desde…</span>
            <span className="block truncate text-xs text-muted-foreground">
              {props.sources.length === 0
                ? "No hay ningún entorno listo del que copiar."
                : "En vez de partir de la rama base y el hook de preparación."}
            </span>
          </span>
          <Switch
            checked={copyFrom}
            disabled={submitting || props.sources.length === 0}
            onCheckedChange={(checked) => setCopyFrom(Boolean(checked))}
          />
        </label>

        {copyFrom ? (
          <div className="flex flex-col gap-3 rounded-md border border-border/60 bg-muted/10 p-3">
            <Field label="Origen">
              <Select value={originId} onValueChange={(value) => value && setOriginId(value)}>
                <SelectTrigger className="w-full" aria-label="Entorno de origen">
                  <SelectValue>
                    {origin === null ? "Elige un entorno" : labelOf(origin)}
                  </SelectValue>
                </SelectTrigger>
                <SelectPopup align="start" alignItemWithTrigger={false}>
                  {props.sources.map((row) => (
                    <SelectItem hideIndicator key={row.id} value={row.id}>
                      {labelOf(row)}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            </Field>

            <Field label="Código">
              <Select
                value={code}
                onValueChange={(value) => value && setCode(value as EnvironmentCloneCodeMode)}
              >
                <SelectTrigger className="w-full" aria-label="Código del clon">
                  <SelectValue>{CODE_LABELS[code]}</SelectValue>
                </SelectTrigger>
                <SelectPopup align="start" alignItemWithTrigger={false}>
                  {(Object.keys(CODE_LABELS) as ReadonlyArray<EnvironmentCloneCodeMode>).map(
                    (mode) => (
                      <SelectItem hideIndicator key={mode} value={mode}>
                        {CODE_LABELS[mode]}
                      </SelectItem>
                    ),
                  )}
                </SelectPopup>
              </Select>
            </Field>

            <Field label="Datos">
              <Select
                value={data}
                onValueChange={(value) => value && setData(value as EnvironmentCloneDataMode)}
              >
                <SelectTrigger className="w-full" aria-label="Datos del clon">
                  <SelectValue>{DATA_LABELS[data]}</SelectValue>
                </SelectTrigger>
                <SelectPopup align="start" alignItemWithTrigger={false}>
                  {(Object.keys(DATA_LABELS) as ReadonlyArray<EnvironmentCloneDataMode>).map(
                    (mode) => (
                      <SelectItem hideIndicator key={mode} value={mode}>
                        {DATA_LABELS[mode]}
                      </SelectItem>
                    ),
                  )}
                </SelectPopup>
              </Select>
            </Field>

            <label className="flex items-center justify-between gap-3 rounded-md border border-border/70 px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="block truncate">Copiar la configuración</span>
                <span className="block truncate text-xs text-muted-foreground">
                  Los ficheros declarados en <code>qa.inheritFiles</code>, reescribiendo sólo las
                  claves <code>TASKIN_*</code>.
                </span>
              </span>
              <Switch
                checked={config}
                disabled={submitting}
                onCheckedChange={(checked) => setConfig(Boolean(checked))}
              />
            </label>
          </div>
        ) : null}

        <p className="text-[11px] text-muted-foreground">
          {props.baseBranch === null ? (
            "Este proyecto no tiene rama base: el servidor rechazará la creación."
          ) : (
            <>
              Nace de <span className="font-mono">{props.baseBranch}</span> y la seguirá. Crear el
              entorno levanta su worktree y ejecuta el hook de preparación del proyecto, que puede
              tardar minutos: el estado pasa a «Listo» cuando termina.
            </>
          )}
        </p>

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Creando…" : "Crear entorno"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
